import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Box,
  Chip,
  IconButton,
  Stack,
  Avatar
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { apiUrl, fileUrl } from "../config/api";

const API_URL = apiUrl("favorites.php");

const CustomCard = ({
  id,
  image,
  title,
  description,
  salary,
  city,
  dateAdded,
  company,
  category,
  onMoreClick,
}) => {
  const fullImageUrl = image ? (image.startsWith('http') ? image : fileUrl(image)) : null;
  const [isFavorite, setIsFavorite] = useState(false);
  const [hovered, setHovered] = useState(false);

  // NEW logic
  let isNew = false;
  if (dateAdded) {
    const parsed = new Date(dateAdded.replace(' ', 'T'));
    if (!isNaN(parsed)) {
      const diff = Date.now() - parsed.getTime();
      isNew = diff < 24 * 60 * 60 * 1000;
    }
  }

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr.replace(' ', 'T'));
    if (isNaN(d)) return '';
    const months = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const displayDate = formatDate(dateAdded);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    fetch(`${API_URL}?user=${userId}&job=${id}`)
      .then((res) => res.json())
      .then((data) => setIsFavorite(data.favorite === true))
      .catch(() => {});
  }, [id]);

  const toggleFavorite = (e) => {
    e.stopPropagation();

    const userId = localStorage.getItem("userId");
    if (!userId) {
      alert("Войдите в аккаунт");
      return;
    }

    fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user: userId, job: id }),
    })
      .then((res) => res.json())
      .then((data) => setIsFavorite(data.favorite))
      .catch(() => {});
  };

  // Чистим описание: убираем HTML, markdown, длинные разделители → превью ~280 символов
  const cleanText = (() => {
    if (!description) return '';
    let text = description
      .replace(/<\/?[^>]+(>|$)/g, '')
      .replace(/\*\*/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/^-{5,}.*$/gm, '')
      .trim();
    if (text.length > 280) {
      text = text.slice(0, 277).trim() + '...';
    }
    return text;
  })();

  return (
    <Card
      onClick={onMoreClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        p: { xs: 1.5, sm: 2.2 },
        borderRadius: { xs: 1, sm: 1.5 },
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",

        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        width: "100%",           // Фиксированная ширина
        minWidth: 0,             // Предотвращает растягивание
        boxSizing: "border-box", // Учитываем padding в ширине

        background: "#ffffff",
        border: "1px solid rgba(0,0,0,0.05)",

        transition: "all 0.35s cubic-bezier(.4,0,.2,1)",

        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 16px 40px rgba(0,0,0,0.08)",
        },
      }}
    >
      {/* 💎 SOFT BACKGROUND LOGO */}
      {fullImageUrl && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            borderRadius: { xs: 1, sm: 1.5 },
            pointerEvents: "none",
          }}
        >
        <Box
          className="bgLogo"
          sx={{
            position: "absolute",
            top: -20,
            right: -20,

            width: { xs: 120, sm: 180 },
            height: { xs: 120, sm: 180 },

            backgroundImage: `url(${fullImageUrl})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",

            opacity: hovered ? 0.40 : 0.30,

            // 🔥 ВАЖНО — убираем grayscale
            filter: "blur(0.1px)",

            // 💎 магия цвета
            mixBlendMode: "multiply",

            transform: hovered
              ? "rotate(-6deg) scale(1.2)"
              : "rotate(-8deg) scale(1.1)",

            transition: "0.5s",
          }}
        />
        </Box>
      )}

      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Stack direction="row" spacing={1.2} alignItems="center">
          {/* LOGO */}
          <Avatar
            src={fullImageUrl}
            alt={title}
            sx={{
              width: { xs: 38, sm: 44 },
              height: { xs: 38, sm: 44 },
              borderRadius: { xs: 1, sm: 1.25 },

              background: "#fff",
              padding: 0.6,

              border: "1px solid rgba(0,0,0,0.06)",
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",

              transition: "0.25s",
              ...(hovered && {
                transform: "scale(1.05)",
              }),
            }}
          />

          <Box sx={{ minWidth: 0 }}>
            {/* COMPANY + NEW */}
            <Stack direction="row" spacing={0.7} alignItems="center">
              <Typography
                sx={{
                  fontSize: { xs: 11, sm: 12 },
                  color: "#6b7280",
                  fontWeight: 500,
                }}
              >
                {company || "Компания"}
              </Typography>

              {category && (
                <Chip
                  label={category}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 600,
                    background: "rgba(99, 102, 241, 0.1)",
                    color: "#6366f1",
                    borderRadius: "4px",
                    maxWidth: 140,
                    ".MuiChip-label": {
                      px: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              )}

              {isNew && (
                <Chip
                  label="NEW"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.4px",
                    background:
                      "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    color: "#fff",
                    borderRadius: 0.5,
                  }}
                />
              )}

            </Stack>

            {/* TITLE */}
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: 14, sm: 15 },
                color: "#111827",

                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: 1.35,
                minHeight: "2.7em",
                maxHeight: "2.7em",
                wordBreak: "break-word",
                width: "100%"
              }}
            >
              {title}
            </Typography>

            {/* LOCATION */}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LocationOnIcon sx={{ fontSize: { xs: 12, sm: 13 }, color: "#9ca3af" }} />
              <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: "#9ca3af" }}>
                {city}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        {/* FAVORITE */}
        <IconButton
          onClick={toggleFavorite}
          size="small"
          sx={{
            background: isFavorite
              ? 'rgba(239, 68, 68, 0.12)'
              : hovered
                ? 'rgba(0,0,0,0.04)'
                : 'transparent',
            transition: 'all 0.25s',
            '&:hover': {
              background: isFavorite
                ? 'rgba(239, 68, 68, 0.18)'
                : 'rgba(239, 68, 68, 0.08)',
              transform: 'scale(1.15)',
            },
            width: 36,
            height: 36,
          }}
        >
          {isFavorite ? (
            <FavoriteIcon sx={{ color: '#ef4444', fontSize: 22 }} />
          ) : (
            <FavoriteBorderIcon
              sx={{
                fontSize: 22,
                color: hovered ? '#ef4444' : '#9ca3af',
              }}
            />
          )}
        </IconButton>
      </Box>

      {/* SALARY */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: { xs: 15, sm: 16 },
          letterSpacing: '-0.3px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          color: salary && salary !== 'Не указана'
            ? '#059669'
            : '#d1d5db',
          transition: 'color 0.25s',
        }}
      >
        {salary}
      </Typography>

      {/* DESCRIPTION */}
      <Typography
        sx={{
          fontSize: { xs: 12.5, sm: 13.5 },
          color: '#6b7280',
          lineHeight: 1.55,

          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',

          /* Inter если загрузился, иначе fallback */
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        {cleanText}
      </Typography>

      {/* TAGS */}
      <Stack direction="row" spacing={0.8} flexWrap="wrap">
        <Chip
          label={city}
          size="small"
          sx={{
            background: "#f3f4f6",
            fontSize: { xs: 10, sm: 11 },
            borderRadius: 0.5,
          }}
        />
        <Chip
          label="Full-time"
          size="small"
          sx={{
            background: "#f3f4f6",
            fontSize: { xs: 10, sm: 11 },
            borderRadius: 0.5,
          }}
        />
      </Stack>

      {/* FOOTER */}
      <Box
        mt="auto"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: "#9ca3af" }}>
          {displayDate}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: 11.5, sm: 12.5 },
            fontWeight: 500,
            color: hovered ? "#4f46e5" : "#9ca3af",
            transition: "0.25s",
          }}
        >
          Open →
        </Typography>
      </Box>
    </Card>
  );
};

export default CustomCard;

